import { ListTodo, Loader2, MessageSquareText, Sparkles } from "lucide-react";
import { useState } from "react";
import { useRecordContext } from "ra-core";
import { Button } from "@/components/ui/button";

import { getActiveWorkspaceId } from "../workspace/activeWorkspace";
import { invokeAi } from "./invokeAi";

interface AiResponse {
  text: string;
  provider: string;
}

/**
 * The first slice of the Gravun AI layer: summarize this record, draft a
 * follow-up. Both are READ-ONLY, the summary is text, and the draft lands
 * in the clipboard/composer for a human to send through the consent gate
 * exactly like a hand-typed message. Nothing here can message a customer or
 * change a record.
 *
 * The edge function reads with the CALLER'S JWT, so the AI sees exactly what
 * this user could open and nothing more. Sandbox output is labelled by the
 * provider so a demo cannot pass as a model.
 */
export const AiAssistCard = ({
  kind = "contact",
  recordId,
}: {
  kind?: "contact" | "deal" | "job";
  recordId?: string | number;
}) => {
  const record = useRecordContext();
  const id = recordId ?? record?.id;
  const workspaceId = getActiveWorkspaceId();
  const [busy, setBusy] = useState<string | null>(null);
  const [result, setResult] = useState<AiResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (id == null) return null;

  const invoke = async (
    action: "summarize" | "draft_followup" | "next_actions",
    extra: Record<string, unknown> = {},
  ) => {
    setBusy(action);
    setError(null);
    setResult(null);
    const { data, error: fnError } = await invokeAi<AiResponse>({
      action,
      kind,
      id,
      workspace_id: workspaceId,
      ...extra,
    });
    setBusy(null);
    if (fnError || !data) {
      setError(fnError ?? "The AI service is not reachable");
      return;
    }
    setResult(data);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-1.5">
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-[12px]"
          disabled={busy !== null}
          onClick={() => invoke("summarize")}
        >
          {busy === "summarize" ? (
            <Loader2 className="mr-1 size-3 animate-spin" />
          ) : (
            <Sparkles className="mr-1 size-3" />
          )}
          Summarize
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-[12px]"
          disabled={busy !== null}
          onClick={() => invoke("draft_followup", { channel: "sms" })}
        >
          {busy === "draft_followup" ? (
            <Loader2 className="mr-1 size-3 animate-spin" />
          ) : (
            <MessageSquareText className="mr-1 size-3" />
          )}
          Draft follow-up
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-[12px]"
          disabled={busy !== null}
          onClick={() => invoke("next_actions")}
        >
          {busy === "next_actions" ? (
            <Loader2 className="mr-1 size-3 animate-spin" />
          ) : (
            <ListTodo className="mr-1 size-3" />
          )}
          Next actions
        </Button>
      </div>

      {error ? <p className="text-[12px] text-destructive">{error}</p> : null}

      {result ? (
        <div className="rounded-md border border-border-subtle bg-muted/30 p-2.5">
          <p className="whitespace-pre-wrap text-[12.5px] leading-relaxed text-foreground">
            {result.text}
          </p>
          <div className="mt-1.5 flex items-center justify-between">
            <span className="text-[10.5px] uppercase tracking-wide text-muted-foreground">
              {result.provider === "sandbox"
                ? "sandbox (no model called)"
                : `via ${result.provider}`}
            </span>
            <button
              type="button"
              className="text-[11.5px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
              onClick={() => {
                navigator.clipboard.writeText(result.text);
              }}
            >
              Copy
            </button>
          </div>
        </div>
      ) : null}

      <p className="text-[11px] text-muted-foreground">
        Drafts are never sent automatically, you review and send through the
        normal composer, consent rules included.
      </p>
    </div>
  );
};
